import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpc } from "@/lib/trpc";
import {
  Bot,
  MessageCircle,
  Send,
  ShoppingBag,
  Sparkles,
  X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { nanoid } from "nanoid";
import { cn } from "@/lib/utils";

type Message = { role: "user" | "assistant"; content: string };
type ChatMode = "salon" | "store";

interface BeautyChatBotProps {
  userId?: number;
  salonId?: number;
  /** Ajusta sugestões e hierarquia visual para a vitrine de produtos. */
  mode?: ChatMode;
  /** Evita sobreposição com um CTA fixo, como o carrinho da loja no celular. */
  hasFloatingAction?: boolean;
}

export function BeautyChatBot({
  userId,
  salonId = 1,
  mode = "salon",
  hasFloatingAction = false,
}: BeautyChatBotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => nanoid());
  const scrollRef = useRef<HTMLDivElement>(null);
  const isStore = mode === "store";

  // Buscar somente os dados públicos da unidade para nomear o atendimento.
  const { data: salonInfo } = trpc.salon.getPublicInfo.useQuery(
    { salonId },
    { enabled: isOpen }
  );

  const agentName = salonInfo?.agentName ?? "Assistente";
  const welcomeMessage =
    salonInfo?.agentWelcomeMessage ??
    `Olá! Sou ${agentName}, assistente virtual. Como posso ajudar você hoje? 😊`;
  const salonName = salonInfo?.name ?? "Beleza & Bronze";
  const suggestedPrompts = isStore
    ? [
        "Quais produtos estão disponíveis?",
        "Quero ajuda para escolher um produto",
        "Quais produtos são indicados para Personal Bronze?",
        "Como faço meu pedido?",
      ]
    : [
        "Quais serviços vocês oferecem?",
        "Como faço um agendamento?",
        "Qual o horário de funcionamento?",
        "Quais os preços?",
      ];

  const sendMessage = trpc.chat.sendMessage.useMutation({
    onSuccess: data => {
      if (data.suppressed) return;
      const reply =
        typeof data.reply === "string" ? data.reply : "Desculpe, não entendi.";
      setMessages(previous => [
        ...previous,
        { role: "assistant", content: reply },
      ]);
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  function submitMessage(message: string) {
    const text = message.trim();
    if (!text || sendMessage.isPending) return;
    setMessages(previous => [...previous, { role: "user", content: text }]);
    setInput("");
    sendMessage.mutate({ salonId, sessionId, message: text, userId });
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitMessage(input);
    }
  }

  const floatingButtonPosition = hasFloatingAction ? "bottom-24" : "bottom-6";

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className={cn(
          "fixed right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform duration-200 hover:scale-105 active:scale-95 sm:right-6",
          floatingButtonPosition
        )}
        aria-label={isOpen ? "Fechar atendimento" : "Abrir atendimento"}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : isStore ? (
          <ShoppingBag className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </button>

      {isOpen && (
        <section
          aria-label={`Conversa com ${agentName}`}
          className="fixed inset-x-0 bottom-0 top-0 z-50 flex h-[100dvh] w-full flex-col overflow-hidden bg-card shadow-2xl sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[min(620px,calc(100dvh-7rem))] sm:w-[390px] sm:rounded-2xl sm:border sm:border-border/50"
        >
          <header
            className="flex items-center gap-3 border-b border-white/10 p-4"
            style={{
              background:
                "linear-gradient(135deg, oklch(0.14 0.025 40), oklch(0.22 0.04 45))",
            }}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
              {isStore ? (
                <ShoppingBag className="h-5 w-5 text-white" />
              ) : (
                <Sparkles className="h-5 w-5 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">
                {agentName}
              </p>
              <p className="truncate text-xs text-white/70">
                {isStore ? "Consultora virtual da loja" : salonName}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="ml-auto flex h-10 w-10 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar atendimento"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <ScrollArea
            className="min-h-0 flex-1 p-4"
            ref={scrollRef as React.RefObject<HTMLDivElement>}
          >
            {messages.length === 0 ? (
              <div className="flex flex-col items-center gap-4 py-5">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Bot className="h-7 w-7 text-primary" />
                </div>
                <div className="max-w-xs text-center">
                  <p className="text-sm font-medium leading-6 text-foreground">
                    {welcomeMessage}
                  </p>
                  {isStore && (
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                      Posso conferir produtos, disponibilidade e registrar seu
                      pedido.
                    </p>
                  )}
                </div>
                <div className="w-full space-y-2">
                  {suggestedPrompts.map(prompt => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() => submitMessage(prompt)}
                      disabled={sendMessage.isPending}
                      className="w-full rounded-xl border border-border/60 p-3 text-left text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground disabled:opacity-60"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message, index) => (
                  <div
                    key={`${message.role}-${index}`}
                    className={cn(
                      "flex",
                      message.role === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-6",
                        message.role === "user"
                          ? "rounded-tr-sm bg-primary text-primary-foreground"
                          : "rounded-tl-sm bg-secondary text-secondary-foreground"
                      )}
                    >
                      {message.role === "assistant" ? (
                        <div className="prose prose-sm max-w-none text-inherit [&_p]:my-0 [&_p+p]:mt-2">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        message.content
                      )}
                    </div>
                  </div>
                ))}
                {sendMessage.isPending && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl rounded-tl-sm bg-secondary px-3.5 py-2.5">
                      <span className="text-xs text-muted-foreground">
                        Respondendo…
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          <div className="border-t border-border/50 bg-background/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={event => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isStore
                    ? "Digite sua dúvida sobre um produto..."
                    : "Digite sua mensagem..."
                }
                className="min-h-11 max-h-28 resize-none text-base sm:text-sm"
                rows={1}
              />
              <Button
                type="button"
                size="icon"
                onClick={() => submitMessage(input)}
                disabled={!input.trim() || sendMessage.isPending}
                className="h-11 w-11 shrink-0"
                aria-label="Enviar mensagem"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
